<script>
import moment from 'moment';
import { mapGetters } from 'vuex';
import { isImminent, isEnded } from '../../utils/Time';
import RelativeTime from '../RelativeTime/RelativeTime.vue';
import EventPositionPrizeMoney from './EventPositionPrizeMoney.vue';

export default {
  name: 'EventHeaderMoreInfoModal',
  components: {
    RelativeTime,
    EventPositionPrizeMoney,
  },
  computed: {
    ...mapGetters({
      isActive: 'FormGuideEventHeader/isMoreInfoModalActive',
      eventData: 'FormGuideEventHeader/getEventData',
      venue: 'FormGuideEventHeader/getVenue',
      meetingName: 'FormGuideEventHeader/getMeetingName',
      prizeMoney: 'FormGuideEventHeader/getPrizeMoney',
      entryCondition: 'FormGuideEventHeader/getEntryCondition',
      weather: 'FormGuideEventHeader/getWeather',
      raceType: 'FormGuideEventHeader/getRaceType',
    }),
    eventInfo() {
      return this.eventData || {};
    },
    isMobile() {
      return this.viewportWidth < 768;
    },
    startTime() {
      return this.eventInfo ? this.eventInfo.start_time : '';
    },
    localStartTime() {
      return this.eventInfo ? moment(this.eventInfo.start_time).format('LT') : '';
    },
    isResulted() {
      return this.eventInfo ? this.eventInfo.is_resulted === 1 : false;
    },
    // is within an hour of starting
    isImminent() {
      return isImminent(this.startTime);
    },
    // start time is in the past
    isEnded() {
      return isEnded(this.startTime);
    },
    hasSprintLane() {
      return this.venue.sprint_lane === 1 ? 'YES' : 'NO';
    },
    roundTemperature() {
      const { weather } = this;
      if (weather) {
        const { temperature, temperature_units } = weather;
        return temperature ? `${Math.round(temperature)}${temperature_units}` : 0;
      }
      return '';
    },
    roundFeelsLike() {
      const { weather } = this;
      if (weather) {
        const { feels_like, temperature_units } = weather;
        return feels_like ? `${Math.round(feels_like)}${temperature_units}` : 0;
      }
      return '';
    },
    orderedPrizeMoney() {
      const { prizeMoney } = this;
      return prizeMoney.length > 0 ? prizeMoney.sort((a, b) => a.position - b.position) : [];
    },
    placeWinners() {
      const { eventData } = this;
      return eventData.place_winners || 3;
    },
    getWeatherCondition() {
      const { weather } = this;
      return weather ? weather.condition : '';
    },
    getWeatherHumidity() {
      const { weather } = this;
      return weather ? weather.humidity : '';
    },
    getWeatherWind() {
      const { weather } = this;
      return weather ? `${weather.wind_direction} ${weather.wind_speed} ${weather.wind_speed_units}` : '';
    },
  },
  methods: {
    toggleModal() {
      this.$store.dispatch('FormGuideEventHeader/toggleMoreInfoModal');
    },
  },
};
</script>

<template>
  <div :class="{ 'more-info-modal--active': isActive }" class="more-info-modal">
    <div class="more-info-modal__header">
      <div class="more-info-modal__header-container">
        <a class="more-info-modal__close" href="#" @click.prevent="toggleModal">
          <div class="more-info-modal__close-icon"></div>
        </a>
        <div class="more-info-modal__header-title">{{ meetingName }} R{{ eventInfo.event_number }}</div>
      </div>
    </div>
    <div class="more-info-modal__details-container">
      <!-- main event information section -->
      <div class="more-info-modal__section">
        <div class="more-info-modal__event-name">{{ eventInfo.name }}</div>
        <div class="more-info-modal__event-time">
          <RelativeTime
            v-if="isImminent || isEnded"
            :brand="`punters`"
            :target-time="startTime"
            :is-resulted="isResulted"
          />
          <div
            v-if="startTime"
            class="more-info-modal__start-time"
          >{{ startTime | sportsCalendarPunters }} {{ eventInfo.start_time | puntersDisplayTime }}</div>
        </div>
      </div>

      <!-- race classification section -->
      <div class="more-info-modal__section">
        <div class="more-info-modal__section-icon event-condition"></div>
        <div class="more-info-modal__section-header">Race Classification</div>
        <div class="more-info-modal__race-class">{{ entryCondition }}</div>
      </div>

      <!-- track info section -->
      <div class="more-info-modal__section">
        <div class="more-info-modal__section-icon track-dist"></div>
        <div class="more-info-modal__section-header">Track Info</div>
        <div class="more-info-modal__section-row">
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">RACE DISTANCE</div>
            <div class="more-info-modal__info-details">{{ eventInfo.distance }}m</div>
          </div>
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">TRACK CONDITION</div>
            <div class="more-info-modal__info-details">Good</div>
          </div>
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">STRAIGHT</div>
            <div class="more-info-modal__info-details">{{ venue.home_straight_length }}m</div>
          </div>
        </div>
        <div v-if="raceType === 'harness'" class="more-info-modal__section-row">
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">FRONT LINE</div>
            <div class="more-info-modal__info-details">{{ eventInfo.number_across_front }}</div>
          </div>
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">START TYPE</div>
            <div class="more-info-modal__info-details">{{ eventInfo.start_type }}</div>
          </div>
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">SPRINT LANE</div>
            <div class="more-info-modal__info-details">{{ hasSprintLane }}</div>
          </div>
        </div>
        <div v-else class="more-info-modal__section-row">
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">RAIL POSITION</div>
            <div class="more-info-modal__info-details"></div>
          </div>
        </div>
        <div class="more-info-modal__section-row">
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">CIRCUMFERENCE</div>
            <div class="more-info-modal__info-details">{{ venue.circumference }}m</div>
          </div>
        </div>
      </div>

      <!-- weather section -->
      <div class="more-info-modal__section">
        <div class="more-info-modal__section-icon weather"></div>
        <div class="more-info-modal__section-row">
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">CURRENT</div>
            <div
              class="more-info-modal__info-details"
            >{{ getWeatherCondition }} {{ roundTemperature }}</div>
          </div>
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">FEELS LIKE</div>
            <div class="more-info-modal__info-details">{{ roundFeelsLike }}</div>
          </div>
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">HUMIDITY</div>
            <div class="more-info-modal__info-details">{{ getWeatherHumidity }}%</div>
          </div>
        </div>
        <div class="more-info-modal__section-row">
          <div class="more-info-modal__info-container">
            <div class="more-info-modal__info-header">WIND</div>
            <div class="more-info-modal__info-details">{{ getWeatherWind }}</div>
          </div>
        </div>
      </div>

      <!-- prize money section -->
      <div class="more-info-modal__section">
        <div class="more-info-modal__section-icon prize-money"></div>
        <div class="more-info-modal__section-header">Prize Money</div>
        <div>
          <div class="more-info-modal__prize-money-container">
            <template v-for="(prize, index) in orderedPrizeMoney">
              <EventPositionPrizeMoney
                :position-no="prize.position"
                :prize-money="prize.value"
                :place-winners="placeWinners"
                :key="index"
              />
            </template>
          </div>
        </div>
      </div>

      <!-- back button -->
      <div class="more-info-modal__btn-container">
        <a href="#" class="more-info-modal__back-btn" @click.prevent="toggleModal">
          <p>Back to form</p>
        </a>
      </div>
    </div>
  </div>
</template>
